import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useData } from "../context/DataContext";
import { ordersAPI, uploadAPI, servicesAPI } from "../api";

const DIAGNOSTIC_SERVICE_CODES = ["diagnostika", "razborka-sborka"];

const Modal = ({ visible, onClose, children }) => {
    if (!visible) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50" onClick={onClose}>
            <div className="bg-white rounded-t-3xl w-full max-h-[80vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-center mb-4">
                    <div className="w-12 h-1 bg-gray-300 rounded-full" />
                </div>
                {children}
            </div>
        </div>
    );
};

export default function ReportScreen() {
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { removeMyOrder, refreshMyOrders, refreshReports } = useData();
    const order = location.state?.order;

    const [reportType, setReportType] = useState(1);
    const [repairSum, setRepairSum] = useState("");
    const [spareSum, setSpareSum] = useState("");
    const [comment, setComment] = useState("");
    const [photos, setPhotos] = useState([]); // File objects
    const [loading, setLoading] = useState(false);

    const [services, setServices] = useState([]);
    const [selectedServices, setSelectedServices] = useState([]);
    const [servicesModalVisible, setServicesModalVisible] = useState(false);
    const [servicesSearch, setServicesSearch] = useState("");
    const [tempSelectedServices, setTempSelectedServices] = useState([]);
    const [loadingServices, setLoadingServices] = useState(false);

    useEffect(() => {
        if (!order) {
            alert("Нет данных о заказе");
            navigate("/my-orders");
            return;
        }
        if (order.technicId) loadServices(order.technicId);
    }, [order]);

    const loadServices = async (technicId) => {
        setLoadingServices(true);
        try {
            const res = await servicesAPI.getServices(technicId);
            const arr = res.data?.kn_price_list || [];
            setServices(
                arr
                    .map((item) => ({
                        name: item.name_list,
                        price: item.price_list,
                    }))
                    .filter((s) => s.name && s.price),
            );
        } catch {
            alert("Не удалось загрузить услуги");
        } finally {
            setLoadingServices(false);
        }
    };

    useEffect(() => {
        if (reportType === 2) {
            setSelectedServices((prev) => prev.filter((code) => DIAGNOSTIC_SERVICE_CODES.includes(code)));
        }
    }, [reportType]);

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        setPhotos((prev) => [...prev, ...files]);
    };

    const openServicesModal = () => {
        if (reportType === 2) {
            setTempSelectedServices(selectedServices.filter((c) => DIAGNOSTIC_SERVICE_CODES.includes(c)));
        } else {
            setTempSelectedServices(selectedServices);
        }
        setServicesSearch("");
        setServicesModalVisible(true);
    };

    const toggleService = (price) => {
        setTempSelectedServices((prev) => (prev.includes(price) ? prev.filter((p) => p !== price) : [...prev, price]));
    };

    const confirmServices = () => {
        let newSelected = tempSelectedServices;
        if (reportType === 2) {
            newSelected = newSelected.filter((c) => DIAGNOSTIC_SERVICE_CODES.includes(c));
        }
        setSelectedServices(newSelected);
        setServicesModalVisible(false);
    };

    const filteredServices = useMemo(() => {
        let base = reportType === 2 ? services.filter((s) => DIAGNOSTIC_SERVICE_CODES.includes(s.price)) : services;
        const searchLower = servicesSearch.toLowerCase();
        const filtered = base.filter((s) => s.name.toLowerCase().includes(searchLower));
        const selected = filtered.filter((s) => tempSelectedServices.includes(s.price));
        const notSelected = filtered.filter((s) => !tempSelectedServices.includes(s.price));
        return [...selected, ...notSelected];
    }, [services, servicesSearch, tempSelectedServices, reportType]);

    const getSelectedServicesText = () => {
        if (!selectedServices.length) return "Выберите услуги";
        const names = services
            .filter((s) => selectedServices.includes(s.price))
            .map((s) => s.name)
            .join(", ");
        return names.length > 50 ? names.substring(0, 50) + "…" : names;
    };

    const handleSubmit = async () => {
        if (!order || !user?.uid_master) return;
        if (reportType !== 3 && (selectedServices.length === 0 || !repairSum.trim())) {
            alert("Выберите услуги и укажите сумму");
            return;
        }
        setLoading(true);
        try {
            const uploadedFileNames = [];
            for (const file of photos) {
                const formData = new FormData();
                formData.append("file", file);
                const currentMonth = new Date().getMonth() + 1;
                formData.append("dirType", `upload/${currentMonth}/${order.orderNumber}`);
                const res = await uploadAPI.uploadPhoto(formData);
                uploadedFileNames.push(res.data.path);
            }

            await ordersAPI.completeOrder({
                orderId: order.orderNumber,
                userId: user.uid_master,
                reportType,
                services: selectedServices,
                repairSum: parseFloat(repairSum) || 0,
                spareSum: parseFloat(spareSum) || 0,
                comment,
                photos: uploadedFileNames,
            });

            removeMyOrder(order.id);
            refreshMyOrders();
            refreshReports();

            alert("Отчёт отправлен");
            navigate("/my-orders");
        } catch (error) {
            console.error(error);
            alert("Не удалось отправить отчёт");
        } finally {
            setLoading(false);
        }
    };

    if (!order) return null;

    return (
        <div className="flex flex-col h-screen bg-white">
            {/* Заголовок */}
            <div className="flex items-center px-4 pt-4 pb-2 border-b">
                <button onClick={() => navigate(-1)} className="mr-3 text-gray-600">
                    ← Назад
                </button>
                <h1 className="text-lg font-bold text-gray-800">Отчёт по заказу №{order.orderNumber}</h1>
            </div>

            {/* Контент с прокруткой */}
            <div className="flex-1 overflow-y-auto px-4 py-4">
                {/* Тип отчёта */}
                <div className="flex mb-4 space-x-2">
                    {[
                        { label: "Работу произвёл", value: 1 },
                        { label: "Только диагностика", value: 2 },
                        { label: "Отмена заказа", value: 3 },
                    ].map((type) => (
                        <button
                            key={type.value}
                            onClick={() => setReportType(type.value)}
                            className={`flex-1 py-2 rounded font-medium ${reportType === type.value ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-700"}`}>
                            {type.label}
                        </button>
                    ))}
                </div>

                {/* Услуги (не для отмены) */}
                {reportType !== 3 && (
                    <div className="mb-4">
                        <label className="block text-gray-700 mb-1">{reportType === 2 ? "Диагностические услуги *" : "Что оказали *"}</label>
                        <div onClick={openServicesModal} className="border border-gray-300 rounded-lg p-3 flex justify-between items-center cursor-pointer">
                            <span className={selectedServices.length ? "text-gray-800" : "text-gray-400"}>{getSelectedServicesText()}</span>
                            <span className="text-gray-400">▼</span>
                        </div>
                    </div>
                )}

                {/* Суммы */}
                {reportType !== 3 && (
                    <>
                        <div className="mb-4">
                            <label className="block text-gray-700 mb-1">{reportType === 2 ? "Сумма диагностики *" : "Общий чек *"}</label>
                            <input
                                type="number"
                                className="w-full border border-gray-300 rounded-lg p-3"
                                value={repairSum}
                                onChange={(e) => setRepairSum(e.target.value)}
                                placeholder="0"
                            />
                        </div>
                        {reportType === 1 && (
                            <div className="mb-4">
                                <label className="block text-gray-700 mb-1">Стоимость запчастей *</label>
                                <input
                                    type="number"
                                    className="w-full border border-gray-300 rounded-lg p-3"
                                    value={spareSum}
                                    onChange={(e) => setSpareSum(e.target.value)}
                                    placeholder="0"
                                />
                            </div>
                        )}
                    </>
                )}

                {/* Комментарий */}
                <div className="mb-4">
                    <label className="block text-gray-700 mb-1">Комментарий</label>
                    <textarea
                        className="w-full border border-gray-300 rounded-lg p-3 min-h-[80px]"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder="Дополнительная информация"
                    />
                </div>

                {/* Фото (не для отмены) */}
                {reportType !== 3 && (
                    <div className="mb-4">
                        <label className="block text-gray-700 mb-1">Фото</label>
                        <label className="border border-dashed border-blue-400 rounded-lg p-4 text-center cursor-pointer block">
                            <span className="text-blue-500">+ Добавить фото</span>
                            <input type="file" multiple accept="image/*" onChange={handleFileSelect} className="hidden" />
                        </label>
                        {photos.length > 0 && (
                            <div className="flex flex-wrap mt-2 gap-2">
                                {photos.map((file, idx) => (
                                    <img key={idx} src={URL.createObjectURL(file)} alt="preview" className="w-20 h-20 rounded-lg object-cover" />
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Кнопки внизу */}
            <div className="flex border-t border-gray-200">
                <button onClick={() => navigate(-1)} className="flex-1 py-4 bg-gray-200 text-gray-600 font-medium">
                    Отмена
                </button>
                <button onClick={handleSubmit} disabled={loading} className="flex-1 py-4 bg-blue-500 text-white font-medium">
                    {loading ? "Отправка..." : "Отправить"}
                </button>
            </div>

            {/* Модалка выбора услуг */}
            <Modal visible={servicesModalVisible} onClose={() => setServicesModalVisible(false)}>
                <h3 className="text-xl font-bold mb-3">Выберите услуги</h3>
                {!loadingServices && (
                    <input
                        type="text"
                        placeholder="Поиск услуг..."
                        className="w-full border rounded-lg px-3 py-2 mb-3"
                        value={servicesSearch}
                        onChange={(e) => setServicesSearch(e.target.value)}
                    />
                )}
                {loadingServices ? (
                    <div className="text-center py-4">Загрузка...</div>
                ) : (
                    <div className="max-h-60 overflow-y-auto mb-4">
                        {filteredServices.map((s) => (
                            <div key={s.price} onClick={() => toggleService(s.price)} className="flex justify-between items-center py-2 border-b border-gray-100 cursor-pointer">
                                <span>{s.name}</span>
                                {tempSelectedServices.includes(s.price) && <span className="text-blue-500">✓</span>}
                            </div>
                        ))}
                    </div>
                )}
                <div className="flex space-x-2">
                    <button onClick={() => setServicesModalVisible(false)} className="flex-1 bg-gray-200 py-3 rounded-lg">
                        Отмена
                    </button>
                    <button onClick={confirmServices} className="flex-1 bg-blue-500 text-white py-3 rounded-lg">
                        Выбрать
                    </button>
                </div>
            </Modal>
        </div>
    );
}
